import React, { useCallback, useEffect, useMemo } from 'react'
import { CheckCircle, MessageSquare, XCircle } from 'react-feather'
import { Flex, Box, Text, Button } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import ReactDOMServer from 'react-dom/server'
import { useDispatch, useSelector } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import styled from 'particles/themes/styled'

import { selectLatestVoteAndDiscussion, voteSubmission, VoteType } from 'state/submissions'
import { theme } from 'theme'
import PopupUserInfo from './PopupUserInfo'
import {
    selectIsMultiSelectMode,
    selectSelectedCampaignUuid,
    makeShowCommentAndVoteSelector,
    SubmissionProgressProps,
    VERIFY_TAB_TYPE,
} from 'state/review'
import { StepState } from 'state/tasks'
import { selectProfileDetails, State } from 'state'
import Icon from 'components/Icon/Icon'
import { SEARCH_PARAMS, getSearchParams } from 'components/utils/url'
import { useHistory } from 'react-router-dom'

const TooltipContainer = styled(Box)`
    min-width: 200px;
`

interface CommentAndVoteSummaryProps {
    submissionProgress: SubmissionProgressProps
    step: StepState
}

const style = {
    icon: {
        width: 16,
        height: 16,
        stroke: theme.colors.doveGrey,
    },
    hideIcon: {
        width: 16,
        height: 16,
        stroke: theme.colors.doveGrey,
        opacity: '40%',
    },
    activeIcon: {
        width: 16,
        height: 16,
        stroke: theme.colors.astronaut,
    },
    hideActiveIcon: {
        width: 16,
        height: 16,
        stroke: theme.colors.astronaut,
        opacity: '40%',
    },
    dot: {
        marginTop: '2px',
        height: 27.72,
        width: 27.72,
        backgroundColor: '#bbb',
        borderRadius: '50%',
        display: 'inline-block',
    },
}

const CommentAndVoteSummary: React.FC<CommentAndVoteSummaryProps> = ({ submissionProgress, step }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const profile = useSelector(selectProfileDetails)
    const isMultiSelectMode = useSelector(selectIsMultiSelectMode)
    const selectShowCommentAndVote = useMemo(makeShowCommentAndVoteSelector, [])
    const showCommentAndVote = useSelector((state: State) => selectShowCommentAndVote(state, { step }))
    const { votes, discussion_members } = useSelector(selectLatestVoteAndDiscussion(submissionProgress.uuid))
    const history = useHistory()

    const isApproved = useMemo(() => votes && votes.approve.findIndex(item => item.uuid === profile.uuid) > -1, [votes])
    const isEditsRequired = useMemo(
        () => votes && votes.request_edits.findIndex(item => item.uuid === profile.uuid) > -1,
        [votes],
    )
    const isRejected = useMemo(() => votes && votes.reject.findIndex(item => item.uuid === profile.uuid) > -1, [votes])
    const isDiscussed = useMemo(
        () => discussion_members.findIndex(item => item.uuid === profile.uuid) > -1,
        [discussion_members],
    )

    useEffect(() => {
        ReactTooltip.rebuild()
    })

    const handleVote = useCallback(
        (vote_type: VoteType): void => {
            if (selectedCampaignUuid) {
                dispatch(
                    voteSubmission({
                        campaignUuid: selectedCampaignUuid,
                        submissionProgressUuid: submissionProgress.uuid,
                        vote_type,
                        profile,
                    }),
                )
            }
        },
        [submissionProgress.uuid, selectedCampaignUuid],
    )

    const handleDiscuss = useCallback(() => {
        const searchParams = getSearchParams({
            add: [
                { key: SEARCH_PARAMS.verifyTab, value: VERIFY_TAB_TYPE.DISCUSSION },
                { key: SEARCH_PARAMS.reviewId, value: submissionProgress.uuid },
            ],
        })
        history.push(searchParams)
    }, [submissionProgress.uuid])

    if (!showCommentAndVote) {
        return null
    }

    return (
        <Box>
            <Flex>
                <Box mt={theme.spacing.xxs} ml={theme.spacing.xxs} width={1 / 4} sx={{ textAlign: 'left' }}>
                    <Button
                        p={0}
                        bg={theme.newColors.neutral50}
                        sx={{
                            ':hover': { cursor: 'pointer' },
                            ':focus': { outline: 'none' },
                        }}
                        data-tip={ReactDOMServer.renderToStaticMarkup(
                            <TooltipContainer>
                                <Text fontSize="2" color={theme.colors.astronaut}>
                                    {newT('submission.review.approveSuggestionByHeader')}
                                </Text>
                                <PopupUserInfo userInfos={votes ? votes.approve : []} />
                            </TooltipContainer>,
                        )}
                        data-for="partiTooltip"
                        onClick={isMultiSelectMode ? undefined : () => handleVote(isApproved ? null : 'approve')}
                    >
                        <Text
                            fontSize="1.3em"
                            color={isApproved ? theme.colors.astronaut : theme.colors.doveGrey}
                            opacity={isMultiSelectMode ? 0.4 : 1}
                        >
                            <CheckCircle
                                style={isMultiSelectMode ? style.hideIcon : isApproved ? style.activeIcon : style.icon}
                            />{' '}
                            {votes ? votes.approve.length : 0}
                        </Text>
                    </Button>
                </Box>
                <Box mt={theme.spacing.xxs} ml={theme.spacing.xxxxs} width={1 / 4} sx={{ textAlign: 'left' }}>
                    <Button
                        p={0}
                        bg={theme.newColors.neutral50}
                        sx={{
                            ':hover': { cursor: 'pointer' },
                            ':focus': { outline: 'none' },
                        }}
                        data-tip={ReactDOMServer.renderToStaticMarkup(
                            <TooltipContainer>
                                <Text fontSize="2" color={theme.colors.astronaut}>
                                    {newT('submission.review.requestEditSuggestionByHeader')}
                                </Text>
                                <PopupUserInfo userInfos={votes ? votes.request_edits : []} />
                            </TooltipContainer>,
                        )}
                        data-for="partiTooltip"
                        onClick={
                            isMultiSelectMode ? undefined : () => handleVote(isEditsRequired ? null : 'request_edits')
                        }
                    >
                        <Text fontSize="1.3em" color={theme.colors.doveGrey} opacity={isMultiSelectMode ? 0.4 : 1}>
                            <Icon.EditCircle
                                style={
                                    isMultiSelectMode ? style.hideIcon : isEditsRequired ? style.activeIcon : style.icon
                                }
                            />{' '}
                            {votes ? votes.request_edits.length : 0}
                        </Text>
                    </Button>
                </Box>
                <Box mt={theme.spacing.xxs} ml={theme.spacing.xxxxs} width={1 / 4} sx={{ textAlign: 'left' }}>
                    <Button
                        p={0}
                        bg={theme.newColors.neutral50}
                        sx={{
                            ':hover': { cursor: 'pointer' },
                            ':focus': { outline: 'none' },
                        }}
                        data-tip={ReactDOMServer.renderToStaticMarkup(
                            <TooltipContainer>
                                <Text fontSize="2" color={theme.colors.astronaut}>
                                    {newT('submission.review.rejectSuggestionByHeader')}
                                </Text>
                                <PopupUserInfo userInfos={votes ? votes.reject : []} />
                            </TooltipContainer>,
                        )}
                        data-for="partiTooltip"
                        onClick={isMultiSelectMode ? undefined : () => handleVote(isRejected ? null : 'reject')}
                    >
                        <Text fontSize="1.3em" color={theme.colors.doveGrey} opacity={isMultiSelectMode ? 0.4 : 1}>
                            <XCircle
                                style={isMultiSelectMode ? style.hideIcon : isRejected ? style.activeIcon : style.icon}
                            />{' '}
                            {votes ? votes.reject.length : 0}
                        </Text>
                    </Button>
                </Box>
                <Box mt={theme.spacing.xxs} ml={theme.spacing.xxxxs} width={1 / 4} sx={{ textAlign: 'left' }}>
                    <Button
                        p={0}
                        bg={theme.newColors.neutral50}
                        sx={{
                            display: isDiscussed ? '' : 'none',
                            ':hover': { cursor: 'pointer' },
                            ':focus': { outline: 'none' },
                        }}
                        data-tip={ReactDOMServer.renderToStaticMarkup(
                            <TooltipContainer>
                                <Text fontSize="2" color={theme.colors.astronaut}>
                                    {newT('submission.review.commentByHeader')}
                                </Text>
                                <PopupUserInfo userInfos={discussion_members} />{' '}
                            </TooltipContainer>,
                        )}
                        data-for="partiTooltip"
                        onClick={isMultiSelectMode ? undefined : handleDiscuss}
                    >
                        <Text fontSize="1.3em" color={theme.colors.astronaut} opacity={isMultiSelectMode ? 0.4 : 1}>
                            <MessageSquare style={isMultiSelectMode ? style.hideActiveIcon : style.activeIcon} />{' '}
                            {discussion_members.length}
                        </Text>
                    </Button>
                    <Button
                        p={0}
                        bg={theme.newColors.neutral50}
                        sx={{
                            display: isDiscussed ? 'none' : '',
                            ':hover': { cursor: 'pointer' },
                            ':focus': { outline: 'none' },
                        }}
                        data-tip={ReactDOMServer.renderToStaticMarkup(
                            <TooltipContainer>
                                <Text fontSize="2" color={theme.colors.astronaut}>
                                    {newT('submission.review.commentByHeader')}
                                </Text>
                                <PopupUserInfo userInfos={discussion_members} />{' '}
                            </TooltipContainer>,
                        )}
                        data-for="partiTooltip"
                        onClick={isMultiSelectMode ? undefined : handleDiscuss}
                    >
                        <Text fontSize="1.3em" color={theme.colors.doveGrey} opacity={isMultiSelectMode ? 0.4 : 1}>
                            <MessageSquare style={isMultiSelectMode ? style.hideIcon : style.icon} />{' '}
                            {discussion_members.length}
                        </Text>
                    </Button>
                </Box>
            </Flex>
        </Box>
    )
}

export default React.memo(CommentAndVoteSummary)
